import * as React from "react";
import { AuthorizationApprovalModal } from "@/components/security/authorization-approval-modal";
import type { Permission, PermissionContext, Role } from "@/lib/permissions";
import type { EligibleApprover } from "@/hooks/use-approval-handler";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ApprovalContextValue {
  /**
   * Check an API response for `requiresApproval`. If it returns `true`,
   * the approval modal is being shown and the caller should stop its flow.
   * If it returns `false`, the response is NOT an approval-required response.
   */
  requestApproval: (
    response: Response,
    data: Record<string, unknown>,
    retryFn: (approvalToken: string) => Promise<void>,
    options?: {
      context?: PermissionContext;
      actionDescription?: string;
    }
  ) => boolean;
  /** Whether the approval modal is currently open. */
  isApprovalPending: boolean;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const ApprovalContext = React.createContext<ApprovalContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function ApprovalProvider({ children }: { children: React.ReactNode }) {
  const [showModal, setShowModal] = React.useState(false);
  const [permission, setPermission] = React.useState<Permission | null>(null);
  const [context, setContext] = React.useState<PermissionContext | undefined>();
  const [approverRoles, setApproverRoles] = React.useState<Role[]>([]);
  const [eligibleApprovers, setEligibleApprovers] = React.useState<
    EligibleApprover[]
  >([]);
  const [actionDescription, setActionDescription] = React.useState<
    string | undefined
  >();

  const retryFnRef = React.useRef<
    ((approvalToken: string) => Promise<void>) | null
  >(null);

  const closeModal = React.useCallback(() => {
    setShowModal(false);
    setPermission(null);
    setContext(undefined);
    setApproverRoles([]);
    setEligibleApprovers([]);
    setActionDescription(undefined);
    retryFnRef.current = null;
  }, []);

  const handleApproved = React.useCallback(
    (approvalToken: string) => {
      const retryFn = retryFnRef.current;
      closeModal();
      if (retryFn) {
        retryFn(approvalToken);
      }
    },
    [closeModal]
  );

  const requestApproval = React.useCallback(
    (
      response: Response,
      data: Record<string, unknown>,
      retryFn: (approvalToken: string) => Promise<void>,
      options?: {
        context?: PermissionContext;
        actionDescription?: string;
      }
    ): boolean => {
      if (
        response.status === 403 &&
        data.requiresApproval === true &&
        typeof data.permission === "string"
      ) {
        retryFnRef.current = retryFn;
        setPermission(data.permission as Permission);
        setApproverRoles(
          (data.approverRoles as Role[]) || [
            "admin",
            "supervising_pharmacist",
            "pharmacist",
          ]
        );
        setEligibleApprovers(
          (data.eligibleApprovers as EligibleApprover[]) || []
        );
        setContext(
          options?.context ??
            (data.context as PermissionContext | undefined) ??
            undefined
        );
        setActionDescription(options?.actionDescription);
        setShowModal(true);
        return true;
      }
      return false;
    },
    []
  );

  const value = React.useMemo<ApprovalContextValue>(
    () => ({
      requestApproval,
      isApprovalPending: showModal,
    }),
    [requestApproval, showModal]
  );

  return (
    <ApprovalContext.Provider value={value}>
      {children}
      {permission && (
        <AuthorizationApprovalModal
          open={showModal}
          onClose={closeModal}
          onApproved={handleApproved}
          permission={permission}
          context={context}
          actionDescription={actionDescription}
          approverRoles={approverRoles}
          eligibleApprovers={eligibleApprovers}
        />
      )}
    </ApprovalContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useApproval(): ApprovalContextValue {
  const ctx = React.useContext(ApprovalContext);
  if (!ctx) {
    throw new Error("useApproval must be used within an ApprovalProvider");
  }
  return ctx;
}
